import type { AxiosEventBus } from '@shared/axios/axiosEventBus';
import { responseHeaderVersionDiffers } from '@shared/axios/helpers';
import type { AxiosResponse } from 'axios';
import axios from 'axios';
import { useEffect } from 'react';

import { useShowNewVersionDeployedOnce } from '@/hooks/useShowNewVersionDeployedOnce';

/**
 * Subscribes to axios events and notifies when a response with header specifying new version is received. It notifies only once.
 * @returns null
 */
export const NewVersionNotifier: React.FC<Pick<AxiosEventBus, 'subscribe' | 'unsubscribe'>> = ({
  subscribe,
  unsubscribe,
}) => {
  const { showNewVersionDeployedMessage } = useShowNewVersionDeployedOnce();

  useEffect(() => {
    const handleResponse = (response: AxiosResponse) => {
      if (responseHeaderVersionDiffers(response.headers)) {
        showNewVersionDeployedMessage();
      }
    };

    const handleResponseError = (error: unknown) => {
      if (
        axios.isAxiosError(error) &&
        error.response?.headers &&
        responseHeaderVersionDiffers(error.response.headers)
      ) {
        showNewVersionDeployedMessage();
      }
    };

    subscribe('response', handleResponse);
    subscribe('responseError', handleResponseError);

    return () => {
      unsubscribe('response', handleResponse);
      unsubscribe('responseError', handleResponseError);
    };
  }, [subscribe, unsubscribe, showNewVersionDeployedMessage]);

  return null;
};
